import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Icon from 'components/AppIcon';
import { supabase } from 'lib/supabase';
import { getDefaultRedirectPath } from 'utils/rbac';

const EmailVerificationConfirmation = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [verificationStatus, setVerificationStatus] = useState('verifying');
  const [errorMessage, setErrorMessage] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [userRole, setUserRole] = useState('client');
  const [redirectPath, setRedirectPath] = useState('/client-profile-setup');
  const [countdown, setCountdown] = useState(5);

  useEffect(() => {
    verifyEmail();
  }, []);

  useEffect(() => {
    if (verificationStatus === 'success' && countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (verificationStatus === 'success' && countdown === 0) {
      navigate(redirectPath);
    }
  }, [verificationStatus, countdown, navigate, redirectPath]);

  const getRedirectForRole = (role) => {
    if (role === 'client') return '/client-profile-setup';
    return getDefaultRedirectPath(role);
  };

  const getRedirectLabel = (role) => {
    if (role === 'client') return 'Profile Setup';
    if (role === 'lawyer') return 'Lawyer Dashboard';
    if (role === 'partner') return 'Partner Portal';
    if (role === 'admin') return 'Admin Dashboard';
    return 'Dashboard';
  };

  const verifyEmail = async () => {
    try {
      const token = searchParams?.get('token') || searchParams?.get('token_hash');
      const type = searchParams?.get('type');

      if (!token) {
        setVerificationStatus('error');
        setErrorMessage('Invalid verification link. No token provided.');
        return;
      }

      const { data, error } = await supabase?.auth?.verifyOtp({
        token_hash: token,
        type: type || 'signup'
      });

      if (error) {
        throw error;
      }

      if (data?.user) {
        setUserEmail(data?.user?.email);

        // Fetch user profile to get role for correct redirect
        const { data: profile } = await supabase
          ?.from('user_profiles')
          ?.select('role')
          ?.eq('id', data?.user?.id)
          ?.single();

        const role = profile?.role || data?.user?.user_metadata?.role || 'client';
        setUserRole(role);
        setRedirectPath(getRedirectForRole(role));
        setVerificationStatus('success');
      } else {
        throw new Error('Verification failed. Please try again.');
      }
    } catch (err) {
      console.error('Verification error:', err);
      setVerificationStatus('error');

      if (err?.message?.includes('expired')) {
        setErrorMessage('This verification link has expired. Please request a new one.');
      } else if (err?.message?.includes('invalid')) {
        setErrorMessage('This verification link is invalid. Please check your email for the correct link.');
      } else {
        setErrorMessage(err?.message || 'Email verification failed. Please try again.');
      }
    }
  };

  const handleRequestNewLink = () => {
    navigate('/login', { state: { showResendVerification: true } });
  };

  const handleContinueToDashboard = () => {
    navigate(redirectPath);
  };

  // Verifying State
  if (verificationStatus === 'verifying') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center space-y-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-4">
              <div className="animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent"></div>
            </div>
            <h1 className="text-2xl font-bold text-text-primary">Verifying Your Email</h1>
            <p className="text-text-secondary">Please wait while we confirm your email address...</p>
          </div>
        </div>
      </div>
    );
  }

  // Success State
  if (verificationStatus === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Success Icon */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-4 animate-bounce">
              <Icon name="CheckCircle" size={48} className="text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-text-primary mb-2">Email Verified!</h1>
            <p className="text-text-secondary">Your account has been successfully activated</p>
          </div>

          {/* Main Card */}
          <div className="bg-white rounded-2xl shadow-xl p-8 space-y-6">
            {/* User Info */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center space-x-3">
                <Icon name="Mail" size={20} className="text-green-600 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-text-secondary mb-1">Verified email address:</p>
                  <p className="text-base font-medium text-text-primary truncate">{userEmail}</p>
                </div>
              </div>
            </div>

            {/* Welcome Message */}
            <div className="space-y-3">
              <h2 className="text-lg font-semibold text-text-primary">Welcome to LawDesk KE!</h2>
              <p className="text-sm text-text-secondary">
                Your account is now active and ready to use. You can now access all platform features including:
              </p>
              <ul className="space-y-2">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0 w-5 h-5 bg-primary-100 rounded-full flex items-center justify-center">
                    <Icon name="Check" size={12} className="text-primary" />
                  </div>
                  <span className="text-sm text-text-primary">Access to legal case management tools</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0 w-5 h-5 bg-primary-100 rounded-full flex items-center justify-center">
                    <Icon name="Check" size={12} className="text-primary" />
                  </div>
                  <span className="text-sm text-text-primary">Connect with verified legal practitioners</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0 w-5 h-5 bg-primary-100 rounded-full flex items-center justify-center">
                    <Icon name="Check" size={12} className="text-primary" />
                  </div>
                  <span className="text-sm text-text-primary">Secure document management and sharing</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0 w-5 h-5 bg-primary-100 rounded-full flex items-center justify-center">
                    <Icon name="Check" size={12} className="text-primary" />
                  </div>
                  <span className="text-sm text-text-primary">Video consultations with lawyers</span>
                </div>
              </ul>
            </div>

            {/* Auto-redirect Notice */}
            <div className="bg-primary-50 rounded-lg p-4 text-center">
              <p className="text-sm text-text-secondary">
                Redirecting to <span className="font-semibold text-primary">{getRedirectLabel(userRole)}</span> in{' '}
                <span className="font-bold text-primary">{countdown}</span> seconds...
              </p>
            </div>

            {/* Continue Button */}
            <button
              onClick={handleContinueToDashboard}
              className="w-full bg-primary text-white py-3 px-4 rounded-lg font-medium hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-smooth flex items-center justify-center space-x-2"
            >
              <Icon name="UserCheck" size={20} />
              <span>Continue to {getRedirectLabel(userRole)}</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Error State
  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Error Icon */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-error-100 rounded-full mb-4">
            <Icon name="XCircle" size={48} className="text-error-600" />
          </div>
          <h1 className="text-3xl font-bold text-text-primary mb-2">Verification Failed</h1>
          <p className="text-text-secondary">We couldn't verify your email address</p>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8 space-y-6">
          {/* Error Message */}
          <div className="bg-error-50 border border-error-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Icon name="AlertCircle" size={20} className="text-error-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-error-700 mb-1">Verification Error</p>
                <p className="text-sm text-error-600">{errorMessage}</p>
              </div>
            </div>
          </div>

          {/* Help Text */}
          <div className="space-y-3">
            <h2 className="text-lg font-semibold text-text-primary">What can you do?</h2>
            <ul className="space-y-2">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-5 h-5 bg-primary-100 rounded-full flex items-center justify-center mt-0.5">
                  <span className="text-xs font-bold text-primary">1</span>
                </div>
                <span className="text-sm text-text-primary">Check your email for the most recent verification link</span>
              </div>
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-5 h-5 bg-primary-100 rounded-full flex items-center justify-center mt-0.5">
                  <span className="text-xs font-bold text-primary">2</span>
                </div>
                <span className="text-sm text-text-primary">Request a new verification email if the link has expired</span>
              </div>
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-5 h-5 bg-primary-100 rounded-full flex items-center justify-center mt-0.5">
                  <span className="text-xs font-bold text-primary">3</span>
                </div>
                <span className="text-sm text-text-primary">Contact support if you continue experiencing issues</span>
              </div>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <button
              onClick={handleRequestNewLink}
              className="w-full bg-primary text-white py-3 px-4 rounded-lg font-medium hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-smooth flex items-center justify-center space-x-2"
            >
              <Icon name="Mail" size={20} />
              <span>Request New Verification Email</span>
            </button>

            <button
              onClick={() => navigate('/login')}
              className="w-full text-primary hover:text-primary-700 font-medium py-2 flex items-center justify-center space-x-2 transition-smooth"
            >
              <Icon name="ArrowLeft" size={16} />
              <span>Back to Login</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationConfirmation;